/**
 * Sites Page - Microsoft 365 Style
 * Manage client sites with comprehensive location details
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Badge,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  LocationRegular,
  SearchRegular,
  MapRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { SearchableDropdown, TablePagination, SlidePanel } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';
import { ghanaRegions } from '../../../configurations/staticData';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filterBar: {
    marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  searchBar: {
    flex: 1,
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface Site {
  id: string;
  name: string;
  site_code?: string;
  gps_code?: string;
  address?: string;
  suburb?: string;
  district?: string;
  region?: string;
  zone_id?: string;
  zone_name?: string;
  site_type_id?: string;
  site_type_name?: string;
  nearest_landmark?: string;
  map_url?: string;
  client_id?: string;
}

interface Client {
  id: string;
  client_name: string;
}

interface Zone {
  id: string;
  name: string;
}

interface SiteType {
  id: string;
  name: string;
}

export const SitesFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [siteTypes, setSiteTypes] = useState<SiteType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    siteCode: '',
    gpsCode: '',
    address: '',
    suburb: '',
    district: '',
    region: '',
    zoneId: '',
    siteTypeId: '',
    nearestLandmark: '',
    mapUrl: '',
  });

  useEffect(() => {
    fetchClients();
    fetchZones();
    fetchSiteTypes();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]); // Fetch when client changes

  const fetchClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchZones = async () => {
    try {
      const response = await ApiClient.get('/get-zones');
      if (response.data.status === 'success') {
        setZones(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchSiteTypes = async () => {
    try {
      const response = await ApiClient.get('/get-site-types');
      if (response.data.status === 'success') {
        setSiteTypes(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchSites = async () => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-sites', {
        params: { client_id: selectedClient },
      });
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch sites');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleOpenDialog = (site?: Site) => {
    if (site) {
      setSelectedSite(site);
      setFormData({
        name: site.name,
        siteCode: site.site_code || '',
        gpsCode: site.gps_code || '',
        address: site.address || '',
        suburb: site.suburb || '',
        district: site.district || '',
        region: site.region || '',
        zoneId: site.zone_id || '',
        siteTypeId: site.site_type_id || '',
        nearestLandmark: site.nearest_landmark || '',
        mapUrl: site.map_url || '',
      });
    } else {
      setSelectedSite(null);
      setFormData({
        name: '',
        siteCode: '',
        gpsCode: '',
        address: '',
        suburb: '',
        district: '',
        region: '',
        zoneId: '',
        siteTypeId: '',
        nearestLandmark: '',
        mapUrl: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.region || !formData.zoneId || !selectedClient) {
      newToast('warning', 'Please fill required fields (Name, Region, Zone, Client)');
      return;
    }

    try {
      const payload = {
        client_id: selectedClient,
        id: selectedSite?.id || '',
        name: formData.name,
        site_code: formData.siteCode,
        gps_code: formData.gpsCode,
        address: formData.address,
        suburb: formData.suburb,
        district: formData.district,
        region: formData.region,
        zone_id: formData.zoneId,
        site_type_id: formData.siteTypeId || null,
        nearest_landmark: formData.nearestLandmark,
        map_url: formData.mapUrl,
      };

      const response = await ApiClient.post('/add-update-site', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Site saved successfully');
        fetchSites();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save site');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedSite) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-site', {
        id: selectedSite.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Site deleted successfully');
        fetchSites();
      } else {
        newToast('error', response.data?.message || 'Failed to delete site');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedSite(null);
  };

  const filteredSites = sites.filter(site => {
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.site_code?.toLowerCase().includes(query) ||
      site.address?.toLowerCase().includes(query) ||
      site.zone_name?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredSites,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Sites</Text>
        <Text className={styles.pageSubtitle}>
          {selectedClient
            ? loading
              ? 'Loading...'
              : `${sites.length} sites`
            : 'Select a client to view sites'}
        </Text>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div style={{ width: '300px' }}>
          <Label>Client</Label>
          <SearchableDropdown
            placeholder="Search client..."
            options={clients.map(client => ({
              id: client.id,
              label: client.client_name,
            }))}
            selectedValue={selectedClient || ''}
            onSelect={value => setSelectedClient(value)}
          />
        </div>

        {selectedClient && (
          <div className={styles.searchBar}>
            <Input
              placeholder="Search sites..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              contentBefore={<SearchRegular />}
              size="medium"
            />
          </div>
        )}
      </div>

      {/* Command Bar */}
      {selectedClient && <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Site" />}

      {/* Sites Table */}
      {!selectedClient ? (
        <div className={styles.emptyState}>
          <LocationRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
          <Text size={500} weight="semibold" style={{ display: 'block' }}>
            Select a client
          </Text>
          <Text style={{ marginTop: '8px', display: 'block' }}>
            Choose a client from the dropdown above to view their sites
          </Text>
        </div>
      ) : (
        <ContentCard noPadding>
          {loading ? (
            <div className={styles.emptyState}>
              <Text>Loading sites...</Text>
            </div>
          ) : filteredSites.length === 0 ? (
            <div className={styles.emptyState}>
              <LocationRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
              <Text size={500} weight="semibold" style={{ display: 'block' }}>
                No sites found
              </Text>
              <Text style={{ marginTop: '8px', display: 'block' }}>
                {searchQuery
                  ? 'Try adjusting your search'
                  : 'Click "Add Site" to create a new site'}
              </Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Site Name</th>
                  <th className={styles.th}>Code</th>
                  <th className={styles.th}>Address</th>
                  <th className={styles.th}>Zone</th>
                  <th className={styles.th}>Type</th>
                  <th className={styles.th}>Region</th>
                  <th className={styles.th} style={{ width: '150px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(site => {
                  const zoneName =
                    site.zone_name || zones.find(z => z.id === site.zone_id)?.name || null;
                  const typeName =
                    site.site_type_name ||
                    siteTypes.find(t => t.id === site.site_type_id)?.name ||
                    null;

                  return (
                    <tr key={site.id} className={styles.tr}>
                      <td className={styles.td}>
                        <Text weight="semibold">{site.name}</Text>
                      </td>
                      <td className={styles.td}>{site.site_code || '—'}</td>
                      <td className={styles.td}>{site.address || '—'}</td>
                      <td className={styles.td}>
                        {zoneName ? (
                          <Badge appearance="tint" color="brand">
                            {zoneName}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={styles.td}>{typeName || '—'}</td>
                      <td className={styles.td}>{site.region || '—'}</td>
                      <td className={styles.td}>
                        <div className={styles.actionButtons}>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<EyeRegular />}
                            onClick={() => {
                              navigate(`/account/site-details/${site.id}`, { state: { site } });
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<EditRegular />}
                            onClick={() => handleOpenDialog(site)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<DeleteRegular />}
                            onClick={() => {
                              setSelectedSite(site);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && filteredSites.length > 0 && (
            <TablePagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </ContentCard>
      )}

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedSite ? 'Edit Site' : 'Add Site'}
        subtitle={selectedSite ? 'Update site details' : 'Create a new site'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedSite ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label required>Site Name</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter site name"
            />
          </div>
          <div className={styles.formField}>
            <Label>Site Code</Label>
            <Input
              value={formData.siteCode}
              onChange={e => setFormData({ ...formData, siteCode: e.target.value })}
              placeholder="Enter site code"
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>GPS Code</Label>
            <Input
              value={formData.gpsCode}
              onChange={e => setFormData({ ...formData, gpsCode: e.target.value })}
              placeholder="Enter GPS code"
            />
          </div>
          <div className={styles.formField}>
            <Label>Suburb</Label>
            <Input
              value={formData.suburb}
              onChange={e => setFormData({ ...formData, suburb: e.target.value })}
              placeholder="Enter suburb"
            />
          </div>
        </div>

        <div className={styles.formField}>
          <Label>Address</Label>
          <Input
            value={formData.address}
            onChange={e => setFormData({ ...formData, address: e.target.value })}
            placeholder="Enter address"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label>District</Label>
            <Input
              value={formData.district}
              onChange={e => setFormData({ ...formData, district: e.target.value })}
              placeholder="Enter district"
            />
          </div>
          <div className={styles.formField}>
            <Label required>Region</Label>
            <SearchableDropdown
              placeholder="Search region..."
              options={ghanaRegions.map(region => ({
                id: region.value,
                label: region.label,
              }))}
              selectedValue={formData.region}
              onSelect={value => setFormData({ ...formData, region: value })}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <Label required>Zone</Label>
            <SearchableDropdown
              placeholder="Search zone..."
              options={zones.map(zone => ({
                id: zone.id,
                label: zone.name,
              }))}
              selectedValue={formData.zoneId}
              onSelect={value => setFormData({ ...formData, zoneId: value })}
            />
          </div>
          <div className={styles.formField}>
            <Label>Site Type</Label>
            <SearchableDropdown
              placeholder="Search site type..."
              options={siteTypes.map(type => ({
                id: type.id,
                label: type.name,
              }))}
              selectedValue={formData.siteTypeId}
              onSelect={value => setFormData({ ...formData, siteTypeId: value })}
            />
          </div>
        </div>

        <div className={styles.formField}>
          <Label>Nearest Landmark</Label>
          <Input
            value={formData.nearestLandmark}
            onChange={e => setFormData({ ...formData, nearestLandmark: e.target.value })}
            placeholder="Enter nearest landmark"
          />
        </div>

        <div className={styles.formField}>
          <Label>Map URL</Label>
          <Input
            value={formData.mapUrl}
            onChange={e => setFormData({ ...formData, mapUrl: e.target.value })}
            placeholder="Enter Google Maps URL"
            contentBefore={<MapRegular />}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedSite?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SitesFluentUI;
